import { useGameStore } from '@/store/gameStore'
import {
  DIVISIONS,
  SEASON,
  PLAYER_CLUB_ID,
  computeStandings,
} from '@/config/leagueConfig'
import { ROSTER_RULES } from '@/config/playersConfig'
import { canEnrollInLeague, enrollInLeague } from '@/game/systems/LeagueSystem'
import { StandingsTable } from '../components/StandingsTable'
import { SectionDeck, EmptyStateRich } from '../components/CourtVision'
import {
  BroadcastHeader,
  VersusBanner,
  MatchResultCard,
  TrophyCabinet,
} from '../components/broadcast'
import { seasonsToTrophyItems } from '../helpers/palmares'
import { Shield, CalendarDays, History, ListOrdered, Lock, Landmark } from 'lucide-react'

export function LeagueTabContent() {
  const { week, league, leagueHistory, players, seasonObjective, boardConfidence } = useGameStore()

  if (!league) {
    const { can, reasons } = canEnrollInLeague()
    return (
      <div>
        <SectionDeck title="Liga de clubes" icon={Shield}>
          <EmptyStateRich
            icon={Shield}
            title="La Liga Regional espera a tu club"
            sub={`Compite contra 7 clubes rivales en temporadas de ${SEASON.matchdays} jornadas: una por semana, con primas por victoria, premios de temporada y ascensos a divisiones superiores.`}
            action={can ? (
              <button className="btn btn-success" onClick={() => enrollInLeague()}>
                Inscribirse en la liga
              </button>
            ) : undefined}
          />

          <ul style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: '0.75rem 0 0', paddingLeft: '1rem' }}>
            <li>Necesitas {ROSTER_RULES.leagueMinPlayers} jugadores en plantilla (2 parejas) &mdash; tienes {players.length}</li>
            <li>Empiezas en {DIVISIONS[4].name}</li>
            <li>Los 2 primeros ascienden; los 2 ultimos descienden</li>
          </ul>

          {!can && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.6rem' }}>
              {reasons.map(r => (
                <span key={r} className="pill pill-warning"><Lock size={10} /> {r}</span>
              ))}
            </div>
          )}
        </SectionDeck>

        {leagueHistory.length > 0 && <Palmares />}
      </div>
    )
  }

  const division = DIVISIONS[league.division]
  const standings = computeStandings(league, 'Mi Club')
  const currentMatchday = Math.min(SEASON.matchdays, Math.max(0, week - league.seasonStartWeek))
  const nextMatchday = currentMatchday + 1

  const nextFixture = league.fixtures.find(
    f => !f.result && (f.homeId === PLAYER_CLUB_ID || f.awayId === PLAYER_CLUB_ID)
  )
  const playerFixtures = league.fixtures
    .filter(f => (f.homeId === PLAYER_CLUB_ID || f.awayId === PLAYER_CLUB_ID) && f.result)
    .sort((a, b) => b.matchday - a.matchday)

  const rivalName = (clubId: string) =>
    clubId === PLAYER_CLUB_ID ? 'Mi Club' : league.clubs.find(c => c.id === clubId)?.name ?? clubId

  return (
    <div>
      <BroadcastHeader
        title={division.name}
        competition="Liga de clubes"
        matchday={{ current: currentMatchday, total: SEASON.matchdays }}
        trophySrc="/assets/broadcast/trophy-league.webp"
        meta={<span>Temporada {league.season}</span>}
      />

      {seasonObjective && (
        <div
          className="row-tile"
          style={{
            marginBottom: '1.25rem',
            alignItems: 'center',
            gap: '0.6rem',
            ...(boardConfidence < 30
              ? { borderColor: 'color-mix(in srgb, var(--danger) 45%, transparent)' }
              : {}),
          }}
        >
          <Landmark size={16} style={{ color: 'var(--gold)', flexShrink: 0 }} />
          <div className="row-tile-main">
            <div className="row-tile-title">Objetivo de la junta: {seasonObjective.label}</div>
            <div className="row-tile-sub">
              {boardConfidence < 30
                ? 'La junta está muy descontenta — peligra tu puesto'
                : boardConfidence < 55
                  ? 'La junta espera ver mejoría'
                  : 'La junta confía en tu gestión'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '120px' }}>
            <div className="progress-track" style={{ flex: 1 }}>
              <div
                className={`progress-fill angled ${boardConfidence >= 55 ? 'success' : boardConfidence >= 30 ? 'warning' : 'danger'}`}
                style={{ width: `${boardConfidence}%` }}
              />
            </div>
            <span className="meta-text" style={{ fontWeight: 700, minWidth: '32px', textAlign: 'right' }}>
              {Math.round(boardConfidence)}%
            </span>
          </div>
        </div>
      )}

      {nextFixture && nextMatchday <= SEASON.matchdays && (
        <div style={{ marginBottom: '1.25rem' }}>
          <VersusBanner
            home={{ name: rivalName(nextFixture.homeId), isPlayer: nextFixture.homeId === PLAYER_CLUB_ID }}
            away={{ name: rivalName(nextFixture.awayId), isPlayer: nextFixture.awayId === PLAYER_CLUB_ID }}
            label={`Jornada ${nextFixture.matchday} · Prima €${division.matchdayWinBonus}`}
            tone="upcoming"
          />
        </div>
      )}

      <SectionDeck title="Clasificación" icon={ListOrdered} meta={division.name}>
        <StandingsTable standings={standings} division={league.division} broadcast />
      </SectionDeck>

      {playerFixtures.length > 0 && (
        <SectionDeck title="Tus resultados" icon={CalendarDays} meta={`${playerFixtures.length} jornada${playerFixtures.length === 1 ? '' : 's'}`}>
          <div className="space-y-1" style={{ maxHeight: '180px', overflowY: 'auto' }}>
            {playerFixtures.map(f => (
              <MatchResultCard
                key={`${f.matchday}-${f.homeId}`}
                matchday={f.matchday}
                homeName={rivalName(f.homeId)}
                awayName={rivalName(f.awayId)}
                homeScore={f.result!.homeMatchWins}
                awayScore={f.result!.awayMatchWins}
                won={f.result!.winnerId === PLAYER_CLUB_ID}
                isHome={f.homeId === PLAYER_CLUB_ID}
              />
            ))}
          </div>
        </SectionDeck>
      )}

      {leagueHistory.length > 0 && <Palmares />}
    </div>
  )
}

function Palmares() {
  const leagueHistory = useGameStore(s => s.leagueHistory)
  const items = seasonsToTrophyItems(leagueHistory)

  return (
    <SectionDeck title="Palmarés" icon={History} meta={`${leagueHistory.length} temporada${leagueHistory.length === 1 ? '' : 's'}`}>
      <TrophyCabinet items={items} />
    </SectionDeck>
  )
}
